import { Building2, Plus, Power, Save, Trash2, UsersRound, WalletCards } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import {
  createAdminOrganization,
  deleteAdminOrganization,
  getAdminOrganizations,
  updateOrganizationCapacity,
  updateOrganizationStatus,
  type AdminOrganization,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalPageHeader,
  InfoPanel,
  SelectField,
  StatePanel,
  TextField,
  canonicalStates,
} from '@urbanik/ui';
import { apiBase } from './api-base';
import './organizations-admin.css';

export function OrganizationsAdminView() {
  const [items, setItems] = useState<readonly AdminOrganization[] | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'cooperative' | 'committee'>('committee');
  const [capacity, setCapacity] = useState('');
  const [capacityDrafts, setCapacityDrafts] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ title: string; text: string } | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingCapacity, setSavingCapacity] = useState('');
  const [busyOrganization, setBusyOrganization] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const load = () =>
    getAdminOrganizations(apiBase).then((value) => {
      setItems(value.items);
      setCapacityDrafts(
        Object.fromEntries(value.items.map((item) => [item.id, String(item.slotCapacity)])),
      );
    });
  useEffect(() => {
    void load().catch((failure) =>
      setError(failure instanceof Error ? failure.message : 'No se pudieron cargar las entidades.'),
    );
  }, []);
  function clearFeedback() {
    setError('');
    setMessage(null);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage(null);
    try {
      const result = await createAdminOrganization(
        apiBase,
        {
          name,
          kind,
          ...(capacity ? { slotCapacity: Number(capacity) } : {}),
        },
        crypto.randomUUID(),
      );
      setName('');
      setCapacity('');
      setMessage({
        title: 'Entidad creada',
        text: `${result.organization.name} fue creada con ${result.organization.slotCapacity} cupos y sin socios, obligaciones ni pagos.`,
      });
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'No se pudo crear la entidad.');
    } finally {
      setSaving(false);
    }
  }
  async function saveCapacity(item: AdminOrganization) {
    const next = Number(capacityDrafts[item.id]);
    const minimumCapacity = item.highestAssignedSlot ?? 0;
    if (!Number.isInteger(next) || next < minimumCapacity || next > 10000) {
      setError(
        minimumCapacity > 0
          ? `La capacidad debe ser un número entero entre ${minimumCapacity} y 10.000 porque el cupo ${minimumCapacity} está asignado.`
          : 'La capacidad debe ser un número entero entre 0 y 10.000.',
      );
      return;
    }
    setSavingCapacity(item.id);
    setError('');
    setMessage(null);
    try {
      const result = await updateOrganizationCapacity(apiBase, item.id, next);
      const direction =
        next > item.slotCapacity
          ? 'aumentada'
          : next < item.slotCapacity
            ? 'reducida'
            : 'confirmada';
      setMessage({
        title: `Capacidad ${direction}`,
        text: `${item.name}: ${item.slotCapacity} → ${result.organization.slotCapacity} cupos. Quedan ${result.organization.availableSlots} disponibles.`,
      });
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'No se pudo actualizar la capacidad.');
    } finally {
      setSavingCapacity('');
    }
  }
  async function changeStatus(item: AdminOrganization) {
    const operationalEnabled = !item.operationalEnabled;
    setBusyOrganization(item.id);
    setError('');
    setMessage(null);
    setConfirmDeleteId('');
    try {
      const result = await updateOrganizationStatus(apiBase, item.id, operationalEnabled);
      setMessage({
        title: operationalEnabled ? 'Entidad activada' : 'Entidad inactivada',
        text: `${result.organization.name} ahora está ${operationalEnabled ? 'operativa' : 'inactiva'}.`,
      });
      await load();
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'No se pudo cambiar el estado de la entidad.',
      );
    } finally {
      setBusyOrganization('');
    }
  }
  async function removeOrganization(item: AdminOrganization) {
    setBusyOrganization(item.id);
    setError('');
    setMessage(null);
    try {
      await deleteAdminOrganization(apiBase, item.id);
      setConfirmDeleteId('');
      setMessage({
        title: 'Entidad eliminada',
        text: `${item.name} fue eliminada porque no contenía socios, movimientos ni historial institucional.`,
      });
      await load();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'No se pudo eliminar la entidad.');
    } finally {
      setBusyOrganization('');
    }
  }
  return (
    <main className="organizations-admin">
      <p className="kicker">ADMINISTRACIÓN · ENTIDADES</p>
      <CanonicalPageHeader
        title="Entidades administradas"
        description="Crea entidades independientes y define su capacidad institucional de cupos."
      />
      {message ? (
        <InfoPanel tone="success" title={message.title}>
          {message.text}
        </InfoPanel>
      ) : null}
      {error ? (
        <InfoPanel tone="danger" title="No se pudo completar la configuración">
          {error}
        </InfoPanel>
      ) : null}
      <section className="organizations-admin__layout">
        <article className="organizations-admin__panel">
          <p className="kicker">NUEVA ENTIDAD</p>
          <h2>Crear una entidad limpia</h2>
          <form onSubmit={(event) => void submit(event)}>
            <TextField
              id="organization-name"
              label="Nombre de la entidad"
              placeholder="Ej. Comité Loreto"
              required
              minLength={3}
              maxLength={160}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                clearFeedback();
              }}
            />
            <aside className="organizations-admin__automatic-code">
              <strong>Código único automático</strong>
              <span>El sistema lo generará a partir del tipo y nombre de la entidad.</span>
            </aside>
            <SelectField
              label="Tipo de entidad"
              value={kind}
              onValueChange={(value) => {
                setKind(value as 'cooperative' | 'committee');
                clearFeedback();
              }}
              options={[
                { value: 'committee', label: 'Comité' },
                { value: 'cooperative', label: 'Cooperativa' },
              ]}
            />
            <TextField
              id="organization-capacity"
              label="Capacidad inicial de cupos"
              hint="Opcional. Puedes definirla o ampliarla posteriormente."
              type="number"
              min={0}
              max={10000}
              step={1}
              value={capacity}
              onChange={(event) => {
                setCapacity(event.target.value);
                clearFeedback();
              }}
            />
            <aside className="organizations-admin__notice">
              <strong>Inicio limpio y aislado</strong>
              <span>No se copian socios, saldos, obligaciones ni comprobantes de COMIVALLE.</span>
            </aside>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              icon={<Plus aria-hidden="true" />}
            >
              {saving ? 'Creando…' : 'Crear entidad'}
            </Button>
          </form>
        </article>
        <section className="organizations-admin__list" aria-label="Entidades disponibles">
          <div>
            <p className="kicker">ENTIDADES DISPONIBLES</p>
            <h2>{items?.length ?? 0} entidades</h2>
          </div>
          {items === null ? (
            <StatePanel state={canonicalStates.find((item) => item.kind === 'loading')!} />
          ) : items.length === 0 ? (
            <StatePanel state={canonicalStates.find((item) => item.kind === 'empty')!} />
          ) : (
            items.map((item) => (
              <article className="organizations-admin__card" key={item.id}>
                <div className="organizations-admin__identity">
                  <span>
                    <Building2 aria-hidden="true" />
                  </span>
                  <div>
                    <strong>{item.name}</strong>
                    <small>
                      {item.code} · {item.kind === 'committee' ? 'Comité' : 'Cooperativa'}
                    </small>
                  </div>
                </div>
                <span
                  className={`organizations-admin__status${item.operationalEnabled ? '' : ' organizations-admin__status--inactive'}`}
                >
                  {item.operationalEnabled ? 'Operativa' : 'Inactiva'}
                </span>
                <dl>
                  <div>
                    <dt>
                      <UsersRound aria-hidden="true" />
                      Socios registrados
                    </dt>
                    <dd>
                      {item.memberCount}
                      <small>
                        {item.activeMemberCount} activos · {item.inactiveMemberCount} inactivos
                      </small>
                    </dd>
                  </div>
                  <div>
                    <dt>
                      <WalletCards aria-hidden="true" />
                      Cupos totales
                    </dt>
                    <dd>
                      {item.slotCapacity}
                      <small>
                        {item.assignedSlotCount} asignados a {item.assignedMemberCount} socios
                      </small>
                    </dd>
                  </div>
                  <div>
                    <dt>Cupos disponibles</dt>
                    <dd>{item.availableSlots}</dd>
                  </div>
                </dl>
                <div className="organizations-admin__capacity">
                  <TextField
                    id={`capacity-${item.id}`}
                    label="Capacidad total de cupos"
                    hint={
                      item.highestAssignedSlot
                        ? `Puedes cambiarla cuantas veces necesites. El mínimo actual es ${item.highestAssignedSlot}, porque ese cupo está asignado.`
                        : 'Puedes cambiarla cuantas veces necesites. Los cupos libres se retiran sin borrar su historial.'
                    }
                    type="number"
                    min={item.highestAssignedSlot ?? 0}
                    max={10000}
                    step={1}
                    value={capacityDrafts[item.id] ?? String(item.slotCapacity)}
                    onChange={(event) =>
                      setCapacityDrafts((current) => ({
                        ...current,
                        [item.id]: event.target.value,
                      }))
                    }
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={
                      savingCapacity === item.id ||
                      Number(capacityDrafts[item.id]) === item.slotCapacity
                    }
                    icon={<Save aria-hidden="true" />}
                    onClick={() => void saveCapacity(item)}
                  >
                    {savingCapacity === item.id ? 'Guardando…' : 'Guardar capacidad'}
                  </Button>
                </div>
                <div className="organizations-admin__actions">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={item.code === 'BASELINE_COMIVALLE' || busyOrganization === item.id}
                    title={
                      item.code === 'BASELINE_COMIVALLE'
                        ? 'La entidad principal debe permanecer operativa.'
                        : undefined
                    }
                    icon={<Power aria-hidden="true" />}
                    onClick={() => void changeStatus(item)}
                  >
                    {busyOrganization === item.id
                      ? 'Procesando…'
                      : item.operationalEnabled
                        ? 'Inactivar entidad'
                        : 'Activar entidad'}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    disabled={item.code === 'BASELINE_COMIVALLE' || busyOrganization === item.id}
                    title={
                      item.code === 'BASELINE_COMIVALLE'
                        ? 'La entidad principal está protegida.'
                        : undefined
                    }
                    icon={<Trash2 aria-hidden="true" />}
                    onClick={() => setConfirmDeleteId(item.id)}
                  >
                    Eliminar entidad
                  </Button>
                </div>
                {item.code === 'BASELINE_COMIVALLE' ? (
                  <small className="organizations-admin__protected">
                    Entidad principal protegida: no puede inactivarse ni eliminarse.
                  </small>
                ) : null}
                {confirmDeleteId === item.id ? (
                  <aside className="organizations-admin__delete-confirmation" role="alert">
                    <strong>¿Eliminar {item.name}?</strong>
                    <span>
                      Solo se eliminará si está vacía. Si ya tiene actividad, el sistema exigirá
                      inactivarla para conservar su historial.
                    </span>
                    <div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setConfirmDeleteId('')}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        disabled={busyOrganization === item.id}
                        onClick={() => void removeOrganization(item)}
                      >
                        {busyOrganization === item.id ? 'Eliminando…' : 'Sí, eliminar'}
                      </Button>
                    </div>
                  </aside>
                ) : null}
              </article>
            ))
          )}
        </section>
      </section>
    </main>
  );
}
